import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Download from '@mui/icons-material/Download';
import Sync from '@mui/icons-material/Sync';
import HelpOutline from '@mui/icons-material/HelpOutline';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const emptySettings = {
  scheduler_mode: 'internal',
  log_prefix: '',
  transfer_method: 'dropbox',
  watch_enabled: false,
  dropbox_folder: '',
  dropbox_enabled: false,
  local_folder: '',
  ftp_host: '',
  ftp_port: 21,
  ftp_username: '',
  ftp_password: '',
  ftp_password_set: false,
  ftp_remote_path: '',
  poll_interval_minutes: 15,
  csv_export: {
    enabled: false,
    destination: 'dropbox',
    dropbox_folder: '',
    local_folder: '',
    ftp_remote_path: '',
    schedule: 'manual',
    category_ids: [],
  },
  asrun_export: {
    enabled: false,
    schedule: 'manual',
    format: 'csv',
  },
  expected_filename: 'dd-mm-yy-hh.m3u',
  example_filename: '',
  example_unprefixed_filename: '',
  pending_imports: 0,
  dropbox_connected: false,
  accepted_filenames: [],
  station_count: 1,
  external_watch_count: 0,
  unprefixed_allowed: true,
  prefix_now_required: false,
};

function transferLabel(method) {
  if (method === 'local_folder') return 'local folder';
  if (method === 'ftp') return 'FTP';
  if (method === 'sftp') return 'SFTP';
  return 'Dropbox';
}

function statusColor(status) {
  if (status === 'applied' || status === 'matched' || status === 'ready') return 'success';
  if (status === 'needs_review' || status === 'suggested') return 'warning';
  if (status === 'rejected' || status === 'unmatched') return 'error';
  return 'default';
}

export default function ExternalSchedulerSettings({
  stationId,
  stations = [],
  categories = [],
  canManage = false,
  onStationChange,
}) {
  const [settings, setSettings] = useState(emptySettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imports, setImports] = useState([]);
  const [activeImport, setActiveImport] = useState(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [assetQuery, setAssetQuery] = useState('');
  const [assetOptions, setAssetOptions] = useState([]);
  const [matchingItemId, setMatchingItemId] = useState(null);
  const [asrunDate, setAsrunDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  });

  const selectedStation = useMemo(
    () => stations.find((station) => String(station.id) === String(stationId)) || null,
    [stations, stationId]
  );

  const loadSettings = useCallback(async () => {
    if (!stationId || !canManage) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/external-scheduler/settings`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setSettings({
        ...emptySettings,
        ...res.data,
        ftp_password: '',
        csv_export: { ...emptySettings.csv_export, ...(res.data.csv_export || {}) },
        asrun_export: { ...emptySettings.asrun_export, ...(res.data.asrun_export || {}) },
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load external scheduler settings' });
    } finally {
      setLoading(false);
    }
  }, [stationId, canManage]);

  const loadImports = useCallback(async () => {
    if (!stationId || !canManage) return;
    try {
      const res = await axios.get(`${API_URL}/api/external-scheduler/imports`, {
        params: { station_id: stationId, limit: 25 },
        withCredentials: true,
      });
      setImports(res.data?.imports || []);
    } catch (error) {
      console.error('Failed to load external scheduler imports', error);
    }
  }, [stationId, canManage]);

  useEffect(() => {
    loadSettings();
    loadImports();
  }, [loadSettings, loadImports]);

  useEffect(() => {
    if (!matchingItemId) return undefined;
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/assets`, {
          params: { station_id: stationId, search: assetQuery || undefined, playout_only: true, limit: 25 },
          withCredentials: true,
        });
        setAssetOptions(Array.isArray(res.data) ? res.data : (res.data?.assets || []));
      } catch (_) {
        setAssetOptions([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [assetQuery, matchingItemId, stationId]);

  if (!canManage) return null;

  const updateCsv = (patch) => {
    setSettings((prev) => ({ ...prev, csv_export: { ...prev.csv_export, ...patch } }));
  };

  const updateAsRun = (patch) => {
    setSettings((prev) => ({ ...prev, asrun_export: { ...prev.asrun_export, ...patch } }));
  };

  const handleSave = async () => {
    if (!stationId) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await axios.put(
        `${API_URL}/api/external-scheduler/settings`,
        {
          station_id: stationId,
          scheduler_mode: settings.scheduler_mode,
          external_scheduler: {
            log_prefix: settings.log_prefix,
            transfer_method: settings.transfer_method,
            watch_enabled: settings.watch_enabled || settings.dropbox_enabled,
            dropbox_folder: settings.dropbox_folder,
            dropbox_enabled: settings.watch_enabled || settings.dropbox_enabled,
            local_folder: settings.local_folder,
            ftp_host: settings.ftp_host,
            ftp_port: settings.ftp_port,
            ftp_username: settings.ftp_username,
            ftp_password: settings.ftp_password || undefined,
            ftp_remote_path: settings.ftp_remote_path,
            poll_interval_minutes: settings.poll_interval_minutes,
            csv_export: settings.csv_export,
            asrun_export: settings.asrun_export,
          },
        },
        { withCredentials: true }
      );
      setSettings({
        ...emptySettings,
        ...res.data,
        ftp_password: '',
        csv_export: { ...emptySettings.csv_export, ...(res.data.csv_export || {}) },
        asrun_export: { ...emptySettings.asrun_export, ...(res.data.asrun_export || {}) },
      });
      setMessage({ type: 'success', text: 'External scheduler settings saved' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !stationId) return;
    setBusy('upload');
    setMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('station_id', stationId);
      const res = await axios.post(`${API_URL}/api/external-scheduler/import`, form, { withCredentials: true });
      await loadImports();
      await loadSettings();
      if (res.data?.status === 'applied') {
        setMessage({ type: 'success', text: `Imported and applied ${file.name}` });
      } else {
        setActiveImport(res.data);
        setMessage({ type: 'warning', text: res.data?.warning_message || 'Import needs review before it can go on air.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to import log' });
    } finally {
      setBusy('');
    }
  };

  const handleSourceImport = async () => {
    setBusy('source');
    setMessage(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/external-scheduler/import-source`,
        { station_id: stationId },
        { withCredentials: true }
      );
      await loadImports();
      const count = res.data?.imported?.length || 0;
      const review = (res.data?.imported || []).find((item) => item.status === 'needs_review');
      if (review) setActiveImport(review);
      const prefixNeeded = (res.data?.skipped || []).filter((row) => row.reason === 'prefix_required').length;
      const source = transferLabel(res.data?.method || settings.transfer_method);
      let text = count ? `Imported ${count} log file(s) from ${source}.` : `No new log files were found on ${source}.`;
      if (prefixNeeded) {
        text += ` ${prefixNeeded} unprefixed file(s) were ignored. Rename them to ${settings.example_filename}.`;
      }
      setMessage({
        type: count ? 'success' : (prefixNeeded ? 'warning' : 'info'),
        text,
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Import failed' });
    } finally {
      setBusy('');
    }
  };

  const handleExportDownload = async () => {
    setBusy('csv');
    try {
      const params = new URLSearchParams({ station_id: stationId });
      if (settings.csv_export.category_ids?.length) {
        params.set('category_ids', settings.csv_export.category_ids.join(','));
      }
      const res = await axios.get(`${API_URL}/api/external-scheduler/export.csv?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      link.href = url;
      link.download = match?.[1] || 'library.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'CSV download failed' });
    } finally {
      setBusy('');
    }
  };

  const handleExportDestination = async () => {
    setBusy('csv-dest');
    try {
      const res = await axios.post(
        `${API_URL}/api/external-scheduler/export`,
        { station_id: stationId, category_ids: settings.csv_export.category_ids },
        { withCredentials: true }
      );
      setMessage({
        type: 'success',
        text: `Uploaded ${res.data.filename} (${res.data.trackCount} tracks) via ${transferLabel(res.data.destination)}.`,
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Library CSV export failed' });
    } finally {
      setBusy('');
    }
  };

  const handleAsRunDownload = async () => {
    setBusy('asrun');
    try {
      const format = settings.asrun_export.format || 'csv';
      const params = new URLSearchParams({
        station_id: stationId,
        date: asrunDate,
        format,
      });
      const res = await axios.get(`${API_URL}/api/external-scheduler/asrun.${format}?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      link.href = url;
      link.download = match?.[1] || `${asrunDate}-playout-asrun.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `Downloaded as-run log for ${asrunDate}.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'As-run download failed' });
    } finally {
      setBusy('');
    }
  };

  const handleAsRunUpload = async () => {
    setBusy('asrun-watch');
    try {
      const res = await axios.post(
        `${API_URL}/api/external-scheduler/export-asrun`,
        {
          station_id: stationId,
          date: asrunDate,
          format: settings.asrun_export.format || 'csv',
        },
        { withCredentials: true }
      );
      setMessage({
        type: 'success',
        text: `Uploaded ${res.data.filename} (${res.data.trackCount} plays) via ${transferLabel(res.data.destination)}.`,
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'As-run upload failed' });
    } finally {
      setBusy('');
    }
  };

  const openImport = async (id) => {
    const res = await axios.get(`${API_URL}/api/external-scheduler/imports/${id}`, { withCredentials: true });
    setActiveImport(res.data);
  };

  const patchItem = async (itemId, body) => {
    const res = await axios.put(
      `${API_URL}/api/external-scheduler/imports/${activeImport.id}/items/${itemId}`,
      body,
      { withCredentials: true }
    );
    setActiveImport(res.data);
    loadImports();
  };

  const applyOrReject = async (action, extra = {}) => {
    const res = await axios.post(
      `${API_URL}/api/external-scheduler/imports/${activeImport.id}/${action}`,
      extra,
      { withCredentials: true }
    );
    setActiveImport(res.data);
    loadImports();
    loadSettings();
    setMessage({
      type: action === 'reject' ? 'info' : 'success',
      text: action === 'reject' ? 'Import rejected.' : 'Log applied.',
    });
  };

  const external = settings.scheduler_mode === 'external';
  const multiStation = stations.length > 1;
  const transferMethod = settings.transfer_method || 'dropbox';
  const sourceReady = transferMethod === 'dropbox'
    ? Boolean(settings.dropbox_folder)
    : transferMethod === 'local_folder'
      ? Boolean(settings.local_folder)
      : Boolean(settings.ftp_host);
  const csvDest = settings.csv_export?.destination || transferMethod;

  return (
    <Card sx={{ bgcolor: 'var(--rs-paper)', border: '2px solid #a78bfa', mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ color: '#a78bfa' }}>
              External Scheduler
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedStation
                ? `Per-station source for ${selectedStation.name}. Default is the internal clock scheduler.`
                : 'Select a station to configure an external music scheduler.'}
            </Typography>
          </Box>
          {settings.pending_imports > 0 && (
            <Chip color="warning" label={`${settings.pending_imports} log(s) need matching`} />
          )}
        </Box>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {settings.station_count > 1 && settings.unprefixed_allowed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You now have {settings.station_count} stations. Unprefixed logs such as{' '}
            <Box component="strong" sx={{ fontFamily: 'monospace' }}>{settings.example_unprefixed_filename || '25-08-26-14.m3u'}</Box>
            {' '}still import for this station. Before you share this folder with another station or turn on its external scheduler, rename files to{' '}
            <Box component="strong" sx={{ fontFamily: 'monospace' }}>{settings.example_filename}</Box>.
          </Alert>
        )}
        {settings.prefix_now_required && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            More than one station is watching this log folder. Files without a station prefix are ignored.
            Rename them to <Box component="strong" sx={{ fontFamily: 'monospace' }}>{settings.example_filename}</Box>.
          </Alert>
        )}

        {stations.length > 1 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Station</InputLabel>
            <Select
              value={stationId || ''}
              label="Station"
              onChange={(e) => onStationChange && onStationChange(e.target.value)}
            >
              {stations.map((station) => (
                <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!stationId ? (
          <Alert severity="info">Select a station above to configure internal or external scheduling.</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Scheduler source</InputLabel>
              <Select
                value={settings.scheduler_mode}
                label="Scheduler source"
                onChange={(e) => setSettings((prev) => ({ ...prev, scheduler_mode: e.target.value }))}
              >
                <MenuItem value="internal">Internal scheduler (clocks and rules)</MenuItem>
                <MenuItem value="external">External scheduler (M3U / any third-party)</MenuItem>
              </Select>
            </FormControl>

            <Accordion sx={{ mb: 2, bgcolor: 'transparent' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HelpOutline fontSize="small" sx={{ color: '#a78bfa' }} />
                  <Typography variant="subtitle2">Setup guide for external music schedulers</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Any product that can write an hour log as M3U, text, or XML can feed RadioSuite. Named tools below are examples; the same matching works for Powergold, Natural Music, StationPlaylist, custom scripts, and anything else.
                  RadioSuite still plays the hour log, fills commercials from Advertising, and records voicetracks. The external product only decides the music order.
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1 }}>
                  <strong>1. Export the library</strong> as CSV (download, Dropbox, local folder, or FTP/SFTP) and import it into the third-party scheduler. Columns are CartID, Title, Artist, Category, Duration, and Filename.<br />
                  <strong>2. Preferred hour logs:</strong> <Box component="code" sx={{ fontFamily: 'monospace' }}>{settings.expected_filename}</Box>
                  {settings.prefix_now_required
                    ? ' — a station prefix is required because another station is watching the same folder.'
                    : settings.station_count > 1
                      ? ' — unprefixed names still work; add the prefix before sharing this folder with another station.'
                      : ' — a prefix is optional while this is the only station.'}
                  {' '}Also accepted: yyyy-mm-dd-hh.m3u, yyyymmddhh.m3u, full-day yyyymmdd.m3u / yyyymmdd.xml / mmddyy.log. You can also upload a file by hand.<br />
                  <strong>3. Example for now:</strong> <Box component="code" sx={{ fontFamily: 'monospace' }}>{settings.example_filename}</Box><br />
                  <strong>4. Each line</strong> should be <Box component="code" sx={{ fontFamily: 'monospace' }}>Artist - Title</Box> (standard M3U <Box component="code" sx={{ fontFamily: 'monospace' }}>#EXTINF</Box> is fine). Optional marker lines: COMMERCIALS, BREAKNOTE, BREAK, STOPSET, COM, TRAFFIC (ad break); VOICETRACK or VT (voicetrack).<br />
                  <strong>5. Matching</strong> uses CartID, artist, title, and filename. Close unique matches are applied automatically. Anything uncertain is held for you to match, skip, or reject the whole log.<br />
                  <strong>6. As-run / played log</strong> — after the hour airs, download or upload what actually played (cart, artist, title, air time, duration, type) as CSV, XML, or JSON so MusicMaster, G Selector, Aiir, or Music1 can reconcile history. Daily upload at 05:10 sends yesterday’s file to the same folder as the library CSV.
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <strong>Any other M3U scheduler</strong> — export hour or full-day M3U/text into Dropbox, a folder RadioSuite can read, or FTP/SFTP. If the scheduler cannot talk to Dropbox, use a local/network folder or FTP. Matching does not require a vendor-specific plugin.<br />
                  <strong>Aiir Scheduler</strong> — Station Outputs → Other M3U. Dropbox is official; Aiir Connect can write a local folder instead. Some StationPlaylist-style exports use mmddyy.log.<br />
                  <strong>Music1 Pro / Cloud</strong> — Dropbox, Google Drive, Box, or a playlist folder. For Drive/Box, install the desktop sync client and point RadioSuite at that local folder. Music1 can send 24 hourly files or one full-day log.<br />
                  <strong>RCS G Selector / SelectorCloud</strong> — Dropbox is not native. Use Interface → Export → Schedule to a local/network folder, or RCS.Connect dropping flat files on FTP/SFTP. Library CSV via File → Print/Export, or the RadioSuite CSV here.<br />
                  <strong>MusicMaster</strong> — Dropbox is not native. Use Dataset → Schedule → Export to Automation (Export Design Editor) or Nexus ExportSchedule into a local/network folder. MusicMaster CS Automated Tasks can export on a timer. Cloud/hosted setups typically drop XML on FTP/SFTP (one file per hour or per day, often yyyymmdd.xml). Map CartID in the export design to RadioSuite’s CartID from the library CSV. Type codes COM/TRAFFIC/STOPSET become ad breaks; VT/VOI become voicetracks.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {external && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Internal clock generation is disabled for this station. Import any M3U, text, .log, or XML hour log
                  named <Box component="strong" sx={{ fontFamily: 'monospace' }}>{settings.expected_filename}</Box>
                  (full-day files such as yyyymmdd.m3u, yyyymmdd.xml, and mmddyy.log are also accepted).
                  {multiStation && ' Other stations can keep using the internal scheduler.'}
                </Alert>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Log file prefix"
                      value={settings.log_prefix}
                      onChange={(e) => setSettings((prev) => ({ ...prev, log_prefix: e.target.value }))}
                      helperText={settings.prefix_now_required
                        ? 'Required on every log filename now that another station is watching this folder.'
                        : 'Used when several stations share a folder. Unprefixed files still import until another station watches the same folder.'}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Preferred filename"
                      value={settings.expected_filename}
                      InputProps={{ readOnly: true }}
                      helperText={`Example: ${settings.example_filename}  ·  Also: yyyy-mm-dd-hh.m3u, yyyymmdd.xml, mmddyy.log`}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>How logs arrive</InputLabel>
                      <Select
                        label="How logs arrive"
                        value={transferMethod}
                        onChange={(e) => setSettings((prev) => ({
                          ...prev,
                          transfer_method: e.target.value,
                          csv_export: {
                            ...prev.csv_export,
                            destination: prev.csv_export.destination === prev.transfer_method
                              ? e.target.value
                              : prev.csv_export.destination,
                          },
                        }))}
                      >
                        <MenuItem value="dropbox">Dropbox</MenuItem>
                        <MenuItem value="local_folder">Local / network folder</MenuItem>
                        <MenuItem value="ftp">FTP</MenuItem>
                        <MenuItem value="sftp">SFTP</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Poll interval (minutes)"
                      value={settings.poll_interval_minutes}
                      onChange={(e) => setSettings((prev) => ({ ...prev, poll_interval_minutes: e.target.value }))}
                    />
                  </Grid>
                  {transferMethod === 'dropbox' && (
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Dropbox logs folder"
                        value={settings.dropbox_folder}
                        onChange={(e) => setSettings((prev) => ({ ...prev, dropbox_folder: e.target.value }))}
                        placeholder="/RadioSuite/Logs/Station"
                        helperText={settings.dropbox_connected ? 'Dropbox is connected.' : 'Connect Dropbox in Settings first.'}
                      />
                    </Grid>
                  )}
                  {transferMethod === 'local_folder' && (
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Local or network folder"
                        value={settings.local_folder}
                        onChange={(e) => setSettings((prev) => ({ ...prev, local_folder: e.target.value }))}
                        placeholder="C:\\Logs\\Station"
                        helperText="Absolute path the RadioSuite server can read. Use this for any scheduler that writes a folder, including Aiir Connect, Music1, G Selector, MusicMaster, or a Google Drive/Box sync directory."
                      />
                    </Grid>
                  )}
                  {(transferMethod === 'ftp' || transferMethod === 'sftp') && (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Host"
                          value={settings.ftp_host}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_host: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Port"
                          value={settings.ftp_port}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_port: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Username"
                          value={settings.ftp_username}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_username: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="password"
                          label="Password"
                          value={settings.ftp_password}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_password: e.target.value }))}
                          helperText={settings.ftp_password_set ? 'Leave blank to keep the saved password.' : 'Required to connect.'}
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          label="Remote folder"
                          value={settings.ftp_remote_path}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_remote_path: e.target.value }))}
                          placeholder="/logs"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      checked={Boolean(settings.watch_enabled || settings.dropbox_enabled)}
                      onChange={(e) => setSettings((prev) => ({
                        ...prev,
                        watch_enabled: e.target.checked,
                        dropbox_enabled: e.target.checked,
                      }))}
                    />
                  }
                  label={`Watch ${transferLabel(transferMethod)} for new hour logs`}
                />

                <Typography variant="subtitle1" sx={{ color: '#a78bfa', mt: 1, mb: 1 }}>Library CSV for cloud schedulers</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Categories to export</InputLabel>
                      <Select
                        multiple
                        label="Categories to export"
                        value={settings.csv_export.category_ids || []}
                        onChange={(e) => updateCsv({ category_ids: e.target.value })}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((id) => {
                              const cat = categories.find((c) => String(c.id) === String(id));
                              return <Chip key={id} size="small" label={cat?.name || id} />;
                            })}
                          </Box>
                        )}
                      >
                        {categories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary">Leave empty to export every category this station can schedule.</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Automatic upload</InputLabel>
                      <Select
                        label="Automatic upload"
                        value={settings.csv_export.schedule}
                        onChange={(e) => updateCsv({ schedule: e.target.value })}
                      >
                        <MenuItem value="manual">Manual only</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly (Monday)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>CSV destination</InputLabel>
                      <Select
                        label="CSV destination"
                        value={csvDest}
                        onChange={(e) => updateCsv({ destination: e.target.value })}
                      >
                        <MenuItem value="dropbox">Dropbox</MenuItem>
                        <MenuItem value="local_folder">Local folder</MenuItem>
                        <MenuItem value="ftp">FTP</MenuItem>
                        <MenuItem value="sftp">SFTP</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {csvDest === 'dropbox' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="CSV Dropbox folder"
                        value={settings.csv_export.dropbox_folder}
                        onChange={(e) => updateCsv({ dropbox_folder: e.target.value })}
                        placeholder={settings.dropbox_folder || '/RadioSuite/Library'}
                      />
                    </Grid>
                  )}
                  {csvDest === 'local_folder' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="CSV local folder"
                        value={settings.csv_export.local_folder}
                        onChange={(e) => updateCsv({ local_folder: e.target.value })}
                        placeholder={settings.local_folder || 'C:\\Library'}
                      />
                    </Grid>
                  )}
                  {(csvDest === 'ftp' || csvDest === 'sftp') && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="CSV remote folder"
                        value={settings.csv_export.ftp_remote_path}
                        onChange={(e) => updateCsv({ ftp_remote_path: e.target.value })}
                        placeholder={settings.ftp_remote_path || '/library'}
                      />
                    </Grid>
                  )}
                </Grid>
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      checked={settings.csv_export.enabled}
                      onChange={(e) => updateCsv({ enabled: e.target.checked })}
                    />
                  }
                  label="Enable scheduled library CSV uploads"
                />

                <Typography variant="subtitle1" sx={{ color: '#a78bfa', mt: 1, mb: 1 }}>As-run / played log for music schedulers</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Actual air times from RadioSuite playout, for MusicMaster history, G Selector, Aiir, or Music1.
                  Choose CSV (G Selector / Music1), XML (MusicMaster / ENCO AirDate + AirTime), or JSON.
                  Files are named <Box component="code" sx={{ fontFamily: 'monospace' }}>PREFIX-playout-asrun-YYYYMMDD.csv</Box> (or .xml / .json)
                  and go to the library CSV upload folder — not the inbound hour-log folder.
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Log date"
                      value={asrunDate}
                      onChange={(e) => setAsrunDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>File format</InputLabel>
                      <Select
                        label="File format"
                        value={settings.asrun_export?.format || 'csv'}
                        onChange={(e) => updateAsRun({ format: e.target.value })}
                      >
                        <MenuItem value="csv">CSV (G Selector, Music1, Aiir)</MenuItem>
                        <MenuItem value="xml">XML (MusicMaster / ENCO)</MenuItem>
                        <MenuItem value="json">JSON</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Automatic as-run upload</InputLabel>
                      <Select
                        label="Automatic as-run upload"
                        value={settings.asrun_export?.schedule || 'manual'}
                        onChange={(e) => updateAsRun({ schedule: e.target.value })}
                      >
                        <MenuItem value="manual">Manual only</MenuItem>
                        <MenuItem value="daily">Daily (yesterday at 05:10)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      checked={Boolean(settings.asrun_export?.enabled)}
                      onChange={(e) => updateAsRun({ enabled: e.target.checked })}
                    />
                  }
                  label="Enable scheduled as-run uploads"
                />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Button variant="contained" component="label" startIcon={<CloudUpload />} disabled={!!busy}>
                    Upload hour log
                    <input hidden type="file" accept=".m3u,.m3u8,.txt,.log,.xml" onChange={handleUpload} />
                  </Button>
                  <Button variant="outlined" startIcon={<Sync />} onClick={handleSourceImport} disabled={!!busy || !sourceReady}>
                    Import from {transferLabel(transferMethod)}
                  </Button>
                  <Button variant="outlined" startIcon={<Download />} onClick={handleExportDownload} disabled={!!busy}>
                    Download library CSV
                  </Button>
                  <Button variant="outlined" onClick={handleExportDestination} disabled={!!busy}>
                    Upload CSV via {transferLabel(csvDest)}
                  </Button>
                  <Button variant="outlined" startIcon={<Download />} onClick={handleAsRunDownload} disabled={!!busy}>
                    Download as-run
                  </Button>
                  <Button variant="outlined" onClick={handleAsRunUpload} disabled={!!busy}>
                    Upload as-run
                  </Button>
                </Box>

                <Typography variant="subtitle1" sx={{ color: '#a78bfa', mb: 1 }}>Recent imports</Typography>
                {imports.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No external logs imported yet.</Typography>
                ) : (
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>File</TableCell>
                          <TableCell>Hour</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Matched</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {imports.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.source_filename || 'upload'}</TableCell>
                            <TableCell>
                              {row.log_date
                                ? (row.log_hour == null
                                  ? `${String(row.log_date).slice(0, 10)} (full day)`
                                  : `${String(row.log_date).slice(0, 10)} ${String(row.log_hour).padStart(2, '0')}:00`)
                                : '—'}
                            </TableCell>
                            <TableCell><Chip size="small" color={statusColor(row.status)} label={row.status} /></TableCell>
                            <TableCell>{row.matched_count}/{row.matched_count + row.unmatched_count}</TableCell>
                            <TableCell>
                              {(row.status === 'needs_review' || row.status === 'ready') && (
                                <Button size="small" onClick={() => openImport(row.id)}>Review</Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#a78bfa' }}>
                {saving ? 'Saving…' : 'Save scheduler source'}
              </Button>
            </Box>
          </>
        )}
      </CardContent>

      <Dialog open={Boolean(activeImport)} onClose={() => setActiveImport(null)} maxWidth="md" fullWidth>
        <DialogTitle>Review imported log</DialogTitle>
        <DialogContent>
          {activeImport?.warning_message && (
            <Alert severity="warning" sx={{ mb: 2 }}>{activeImport.warning_message}</Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {activeImport?.source_filename} · {activeImport?.status}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Log line</TableCell>
                <TableCell>Match</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(activeImport?.items || []).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.position + 1}</TableCell>
                  <TableCell>
                    {item.item_type === 'asset'
                      ? `${item.raw_artist ? `${item.raw_artist} — ` : ''}${item.raw_title}`
                      : item.raw_title}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusColor(item.match_status)}
                      label={
                        item.match_status === 'matched'
                          ? `${item.matched_artist || ''} - ${item.matched_title || ''}`.trim()
                          : item.match_status === 'suggested'
                            ? `Suggested: ${item.suggested_artist || ''} - ${item.suggested_title || ''}`.trim()
                            : item.match_status
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {item.item_type === 'asset' && item.match_status !== 'matched' && item.match_status !== 'skipped' && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', minWidth: 260 }}>
                        {item.suggested_asset_id && (
                          <Button size="small" onClick={() => patchItem(item.id, { action: 'accept_suggestion' })}>
                            Accept guess
                          </Button>
                        )}
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 180, flex: 1 }}
                          options={assetOptions}
                          getOptionLabel={(option) => `${option.artist || 'Unknown'} - ${option.title || ''}`}
                          onOpen={() => setMatchingItemId(item.id)}
                          onInputChange={(_, value) => setAssetQuery(value)}
                          onChange={(_, value) => {
                            if (value?.id) patchItem(item.id, { action: 'match', asset_id: value.id });
                          }}
                          renderInput={(params) => <TextField {...params} label="Match to library" />}
                        />
                        <Button size="small" color="inherit" onClick={() => patchItem(item.id, { action: 'skip' })}>
                          Skip
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveImport(null)}>Close</Button>
          {activeImport?.status !== 'applied' && activeImport?.status !== 'rejected' && (
            <>
              <Button color="inherit" onClick={() => applyOrReject('reject')}>Reject entire log</Button>
              <Button onClick={() => applyOrReject('apply', { skip_unmatched: true })}>
                Apply matched, skip the rest
              </Button>
              <Button variant="contained" onClick={() => applyOrReject('apply')} disabled={!activeImport?.can_apply}>
                Apply log
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
}
